/** Durable receipts are committed in the same database transaction as their effects. */
export const LEADER_WRITE_TABLE = "__haniverse_applied_writes";
export const LEADER_WRITE_RETENTION_MS = 10 * 60_000;
const PRUNE_INTERVAL_MS = 60_000;

export interface LeaderWriteConnection {
  execAsync(source: string): Promise<void>;
  getFirstAsync(source: string, ...params: unknown[]): Promise<unknown>;
  runAsync(source: string, ...params: unknown[]): Promise<unknown>;
}

/** An explicit envelope avoids collisions with user column names and preserves blobs/integers. */
type EncodedValue =
  | ["undefined"]
  | ["scalar", string | number | boolean | null]
  | ["bigint", string]
  | ["bytes", number[]]
  | ["array", EncodedValue[]]
  | ["object", [string, EncodedValue][]];

function encodeValue(value: unknown): EncodedValue {
  if (value === undefined) {
    return ["undefined"];
  }
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return ["scalar", value];
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return ["scalar", value];
  }
  if (typeof value === "bigint") {
    return ["bigint", value.toString()];
  }
  if (value instanceof Uint8Array) {
    return ["bytes", [...value]];
  }
  if (Array.isArray(value)) {
    return ["array", value.map((entry) => encodeValue(entry))];
  }
  if (
    typeof value === "object" &&
    value !== null &&
    (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
  ) {
    return ["object", Object.entries(value).map(([key, entry]) => [key, encodeValue(entry)])];
  }
  throw new TypeError("Unsupported database result in durable write receipt");
}

function decodeValue(value: EncodedValue): unknown {
  switch (value[0]) {
    case "undefined": {
      return undefined;
    }
    case "scalar": {
      return value[1];
    }
    case "bigint": {
      return BigInt(value[1]);
    }
    case "bytes": {
      return new Uint8Array(value[1]);
    }
    case "array": {
      return value[1].map((entry) => decodeValue(entry));
    }
    case "object": {
      return Object.fromEntries(value[1].map(([key, entry]) => [key, decodeValue(entry)]));
    }
  }
}

export function createLeaderWriteLedger(connection: () => LeaderWriteConnection) {
  let lastPrunedAt = 0;

  async function prune(): Promise<void> {
    const now = Date.now();
    if (now - lastPrunedAt < PRUNE_INTERVAL_MS) {
      return;
    }
    await connection().runAsync("DELETE FROM __haniverse_applied_writes WHERE applied_at < ?", [
      now - LEADER_WRITE_RETENTION_MS,
    ]);
    lastPrunedAt = now;
  }

  return {
    finish: async (writeId: string, result: unknown) => {
      await connection().runAsync(
        "INSERT INTO __haniverse_applied_writes (write_id, applied_at, result_json) VALUES (?, ?, ?)",
        [writeId, Date.now(), JSON.stringify(encodeValue(result))],
      );
    },
    /**
     * `createsTable`: a local database's ledger creates its own table. A synced replica must not
     * (no device writes schema there); it receives the table from its cloud copy, where the server
     * creates it (see `actor-cloud-schema-service.ts`). Its rows stay local either way: the sync
     * opener lists the table in `tablesIgnore`.
     */
    initialize: async ({ createsTable }: { createsTable: boolean }) => {
      if (createsTable) {
        await connection().execAsync(
          "CREATE TABLE IF NOT EXISTS __haniverse_applied_writes (write_id TEXT PRIMARY KEY, applied_at INTEGER NOT NULL, result_json TEXT)",
        );
      }
      lastPrunedAt = 0;
      await prune();
    },
    prune,
    read: async (
      writeId: string,
    ): Promise<{ applied: false } | { applied: true; result: unknown }> => {
      const row = await connection().getFirstAsync(
        "SELECT result_json FROM __haniverse_applied_writes WHERE write_id = ? AND result_json IS NOT NULL",
        [writeId],
      );
      if (typeof row !== "object" || row === null) {
        return { applied: false };
      }
      const json: unknown = Reflect.get(row, "result_json");
      if (typeof json !== "string") {
        return { applied: false };
      }
      const parsed: unknown = JSON.parse(json);
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- private codec, written only by finish() in this module
      const encoded = parsed as EncodedValue;
      return { applied: true, result: decodeValue(encoded) };
    },
  };
}
